import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  MdCheck,
  MdClose,
  MdOutlineEventBusy,
  MdOutlinePerson,
  MdRestartAlt,
} from "react-icons/md";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import EmptyState from "components/ui/EmptyState";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";
import { authService } from "services/authService";
import { classMgmtService } from "services/admin/classMgmtService";
import { addDays, formatWeekRangeLabel, getWeekStart } from "utils/weekDate";
import type {
  ClassListItem,
  ClassSession,
  LeaveRequestResponse,
  LeaveRequestStatus,
} from "types/domain";

type StatusFilter = "ALL" | LeaveRequestStatus;

const STATUS_TONE: Record<
  LeaveRequestStatus,
  "warning" | "success" | "danger"
> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
};

/**
 * Class-manager (QLL) screen to review students' leave requests
 * ("xin nghỉ phép"). There is no single "all my classes' requests" backend
 * endpoint, so this page uses a class picker (same pattern as
 * SessionHub/StaffAttendance's class filter) and fetches one class at a
 * time.
 */
export default function LeaveRequestsPage(): JSX.Element {
  const { t, locale } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = React.useMemo(() => authService.getStoredUser(), []);

  // Defense in depth: this screen is only meaningful for staff who can
  // manage classes. The real authorization boundary is enforced by the
  // backend (403 for non-managers of the class), but a STUDENT should never
  // even see the class picker or any leave-request data client-side.
  const isStudent = currentUser?.role === "STUDENT";

  const ALL_CLASSES = "";

  const [classes, setClasses] = React.useState<ClassListItem[]>([]);
  const [classesLoading, setClassesLoading] = React.useState(true);
  const [selectedClassId, setSelectedClassId] = React.useState<string>(
    searchParams.get("classId") || ALL_CLASSES
  );
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("ALL");
  // Default to this week only so the list doesn't get overwhelmed with
  // every past request — can be turned off to see the full history.
  const [thisWeekOnly, setThisWeekOnly] = React.useState(true);
  const weekStart = React.useMemo(() => getWeekStart(new Date()), []);
  const weekEnd = React.useMemo(() => addDays(weekStart, 7), [weekStart]);

  const [requests, setRequests] = React.useState<LeaveRequestResponse[]>([]);
  const [requestsLoading, setRequestsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionById, setSessionById] = React.useState<
    Record<string, ClassSession>
  >({});
  const [reviewingId, setReviewingId] = React.useState<string | null>(null);
  // Deep-linked from a notification (?requestId=...): scroll to and
  // highlight that specific request once the list has loaded.
  const highlightedRequestId = React.useMemo(
    () => searchParams.get("requestId"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const requestRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    if (isStudent) {
      setClassesLoading(false);
      return;
    }
    let active = true;
    classMgmtService
      .getAll({ limit: 200 })
      .then((res) => {
        if (!active) return;
        // TEACHER only manages a subset of classes; MANAGER/SYSTEM_ADMIN
        // can review any class (backend allows it), so show the full list.
        const role = currentUser?.role;
        const items =
          role === "MANAGER" || role === "SYSTEM_ADMIN"
            ? res.items
            : res.items.filter((cls) =>
                cls.managers.some((m) => m.id === currentUser?.id)
              );
        setClasses(items);
      })
      .catch(() => setClasses([]))
      .finally(() => {
        if (active) setClassesLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent]);

  const loadRequests = React.useCallback(async () => {
    if (selectedClassId === ALL_CLASSES && classes.length === 0) {
      setRequests([]);
      return;
    }
    setRequestsLoading(true);
    setError(null);
    try {
      const statusParam = statusFilter === "ALL" ? undefined : statusFilter;
      let items: LeaveRequestResponse[];
      if (selectedClassId === ALL_CLASSES) {
        // No single "all my classes' requests" backend endpoint exists, so
        // fetch every visible class in parallel and merge, newest first.
        const perClass = await Promise.all(
          classes.map((cls) =>
            classMgmtService
              .listLeaveRequests(cls.id, { status: statusParam, limit: 100 })
              .then((res) => res.items)
              .catch(() => [])
          )
        );
        items = perClass
          .flat()
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
      } else {
        const res = await classMgmtService.listLeaveRequests(selectedClassId, {
          status: statusParam,
          limit: 100,
        });
        items = res.items;
      }
      setRequests(items);

      // The response doesn't embed session title/time, so resolve them
      // separately for whichever sessions aren't already cached.
      const missingSessionIds = Array.from(
        new Set(
          items
            .map((item) => item.session_id)
            .filter((id): id is string => !!id && !sessionById[id])
        )
      );
      if (missingSessionIds.length > 0) {
        const sessions = await Promise.all(
          missingSessionIds.map((id) =>
            classMgmtService.getSessionById(id).catch(() => null)
          )
        );
        setSessionById((prev) => {
          const next = { ...prev };
          sessions.forEach((session) => {
            if (session) next[session.id] = session;
          });
          return next;
        });
      }
    } catch (err) {
      setError((err as Error).message || t("leaveRequests.loadError"));
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, statusFilter, classes]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  React.useEffect(() => {
    if (!highlightedRequestId || requestsLoading) return;
    const node = requestRefs.current[highlightedRequestId];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedRequestId, requests, requestsLoading]);

  React.useEffect(() => {
    if (selectedClassId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("classId", selectedClassId);
          return next;
        },
        { replace: true }
      );
    }
  }, [selectedClassId, setSearchParams]);

  const handleReview = async (
    requestId: string,
    status: "PENDING" | "APPROVED" | "REJECTED"
  ) => {
    setReviewingId(requestId);
    try {
      const updated = await classMgmtService.reviewLeaveRequest(
        requestId,
        status
      );
      setRequests((prev) =>
        prev.map((item) => (item.id === requestId ? updated : item))
      );
    } catch (err) {
      setError((err as Error).message || t("leaveRequests.reviewError"));
    } finally {
      setReviewingId(null);
    }
  };

  const classById = React.useMemo(() => {
    const map: Record<string, ClassListItem> = {};
    classes.forEach((cls) => {
      map[cls.id] = cls;
    });
    return map;
  }, [classes]);

  // Sorted newest-first by creation date; optionally limited to this week
  // (by submission date) so the list doesn't get overwhelmed with history.
  const visibleRequests = React.useMemo(() => {
    const sorted = [...requests].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    if (!thisWeekOnly) return sorted;
    return sorted.filter((req) => {
      const createdAt = new Date(req.created_at);
      return createdAt >= weekStart && createdAt < weekEnd;
    });
  }, [requests, thisWeekOnly, weekStart, weekEnd]);

  const statusLabel = (status: LeaveRequestStatus): string => {
    if (status === "PENDING") return t("leaveRequests.statusPending");
    if (status === "APPROVED") return t("leaveRequests.statusApproved");
    return t("leaveRequests.statusRejected");
  };

  const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return "-";
    const date = new Date(value);
    const time = date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
    const day = date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return t("leaveRequests.timeOnDate", { time, day });
  };

  if (isStudent) {
    return (
      <div className="mt-6">
        <EmptyState
          icon={<MdOutlineEventBusy className="h-12 w-12" />}
          title={t("leaveRequests.notAllowed")}
        />
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-3">
      <SectionHeader
        title={t("leaveRequests.title")}
        description={t("leaveRequests.description")}
        icon={<MdOutlineEventBusy className="h-6 w-6" />}
        className="!mb-0"
      />

      <Card extra="w-full p-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedClassId}
            onChange={(e) => setSelectedClassId(e.target.value)}
            disabled={classesLoading || classes.length === 0}
            className="min-w-[180px] rounded-lg border border-line px-3 py-1.5 text-sm text-ink focus:border-brand-500 focus:outline-none dark:bg-canvas"
          >
            <option value={ALL_CLASSES}>
              {classes.length === 0
                ? t("leaveRequests.noClasses")
                : t("leaveRequests.allClasses")}
            </option>
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>

          <div className="h-5 w-px bg-gray-200 dark:bg-white/10" />

          <div className="flex flex-wrap gap-1.5">
            {(["ALL", "PENDING", "APPROVED", "REJECTED"] as StatusFilter[]).map(
              (status) => (
                <ActionButton
                  key={status}
                  variant={statusFilter === status ? "primary" : "ghost"}
                  size="sm"
                  className={
                    statusFilter === status
                      ? "!px-3 !py-1"
                      : "bg-surface-muted !px-3 !py-1 text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                  }
                  onClick={() => setStatusFilter(status)}
                >
                  {status === "ALL"
                    ? t("leaveRequests.filterAll")
                    : statusLabel(status)}
                </ActionButton>
              )
            )}
          </div>

          <div className="h-5 w-px bg-gray-200 dark:bg-white/10" />

          <ActionButton
            variant={thisWeekOnly ? "primary" : "ghost"}
            size="sm"
            className={
              thisWeekOnly
                ? "!px-3 !py-1"
                : "bg-surface-muted !px-3 !py-1 text-gray-600 hover:bg-surface-hover dark:text-gray-300"
            }
            title={formatWeekRangeLabel(weekStart)}
            onClick={() => setThisWeekOnly((v) => !v)}
          >
            {thisWeekOnly
              ? t("leaveRequests.thisWeekOnly")
              : t("leaveRequests.showAll")}
          </ActionButton>
        </div>
      </Card>

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <Card extra="w-full p-3">
        {requestsLoading ? (
          <p className="p-2 text-sm text-ink-muted">
            {t("leaveRequests.loading")}
          </p>
        ) : visibleRequests.length === 0 ? (
          <EmptyState
            icon={<MdOutlineEventBusy className="h-12 w-12" />}
            title={t("leaveRequests.empty")}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs font-semibold uppercase text-ink-muted">
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnIndex")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnStudent")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnSession")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnReason")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnSubmittedAt")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnReviewer")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 font-semibold">
                    {t("leaveRequests.columnStatus")}
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 text-right font-semibold">
                    {t("leaveRequests.columnActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {visibleRequests.map((req, index) => {
                  const session = req.session_id
                    ? sessionById[req.session_id]
                    : undefined;
                  const isHighlighted = req.id === highlightedRequestId;
                  const sessionLabel =
                    req.class_id && classById[req.class_id]
                      ? `${classById[req.class_id].name} • ${
                          session
                            ? `${session.title} — ${formatDateTime(
                                session.start_time
                              )}`
                            : t("leaveRequests.notScheduled")
                        }`
                      : session
                      ? `${session.title} — ${formatDateTime(
                          session.start_time
                        )}`
                      : t("leaveRequests.notScheduled");
                  const reviewerLabel = req.reviewer
                    ? `${req.reviewer.full_name}${
                        req.reviewed_at
                          ? ` (${formatDateTime(req.reviewed_at)})`
                          : ""
                      }`
                    : "-";
                  return (
                    <tr
                      key={req.id}
                      ref={(node) => {
                        requestRefs.current[req.id] = node;
                      }}
                      className={`transition-colors ${
                        isHighlighted
                          ? "bg-brand-50/60 ring-2 ring-inset ring-brand-400 dark:bg-brand-500/10"
                          : ""
                      }`}
                    >
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-ink-subtle">
                        {index + 1}
                      </td>
                      <td className="max-w-[160px] px-2 py-2">
                        <div className="flex items-center gap-2">
                          {req.user.avatar_url ? (
                            <img
                              src={req.user.avatar_url}
                              alt={req.user.full_name}
                              className="h-7 w-7 shrink-0 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-muted text-ink-muted">
                              <MdOutlinePerson className="h-4 w-4" />
                            </div>
                          )}
                          <p
                            className="truncate font-semibold text-ink"
                            title={req.user.full_name}
                          >
                            {req.user.full_name}
                          </p>
                        </div>
                      </td>
                      <td
                        className="max-w-[220px] truncate px-2 py-2 text-xs text-gray-600 dark:text-gray-300"
                        title={sessionLabel}
                      >
                        {sessionLabel}
                      </td>
                      <td
                        className="max-w-[180px] truncate px-2 py-2 text-gray-600 dark:text-gray-300"
                        title={req.content || undefined}
                      >
                        {req.content || "-"}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-ink-muted">
                        {formatDateTime(req.created_at)}
                      </td>
                      <td
                        className="max-w-[160px] truncate px-2 py-2 text-xs text-ink-muted"
                        title={reviewerLabel}
                      >
                        {reviewerLabel}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2">
                        <StatusBadge
                          label={statusLabel(req.status)}
                          tone={STATUS_TONE[req.status]}
                        />
                      </td>
                      <td className="whitespace-nowrap px-2 py-2">
                        {/*
                          Managers can freely move a request between
                          pending/approved/rejected at any time (e.g. to
                          correct a mistaken decision), not just once while
                          PENDING.
                        */}
                        <div className="flex justify-end gap-1">
                          {req.status !== "APPROVED" && (
                            <ActionButton
                              variant="primary"
                              size="sm"
                              className="!p-1.5"
                              title={t("leaveRequests.approve")}
                              disabled={reviewingId === req.id}
                              onClick={() => handleReview(req.id, "APPROVED")}
                            >
                              <MdCheck className="h-4 w-4" />
                            </ActionButton>
                          )}
                          {req.status !== "REJECTED" && (
                            <ActionButton
                              variant="danger"
                              size="sm"
                              className="!p-1.5"
                              title={t("leaveRequests.reject")}
                              disabled={reviewingId === req.id}
                              onClick={() => handleReview(req.id, "REJECTED")}
                            >
                              <MdClose className="h-4 w-4" />
                            </ActionButton>
                          )}
                          {req.status !== "PENDING" && (
                            <ActionButton
                              variant="ghost"
                              size="sm"
                              title={t("leaveRequests.resetToPending")}
                              disabled={reviewingId === req.id}
                              className="bg-surface-muted !p-1.5 text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                              onClick={() => handleReview(req.id, "PENDING")}
                            >
                              <MdRestartAlt className="h-4 w-4" />
                            </ActionButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
